import api from './api';
import { API_ENDPOINTS } from './config';

/**
 * Master Data Service
 * Handles fetching of reference/dropdown data like categories, roles, skills, etc.
 */

/**
 * Fetch all equipment categories
 * @returns {Promise<Object>} { success, data: [{category_id, name}], error }
 */
export const getEquipmentCategories = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.MASTER_DATA.CATEGORIES);
    return response;
  } catch (error) {
    console.error('Get Equipment Categories Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch equipment categories',
    };
  }
};

/**
 * Fetch all checklist templates
 * @returns {Promise<Object>} { success, data: [{checklist_id, checklist_text}], error }
 */
export const getChecklistTemplates = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.MASTER_DATA.CHECKLISTS);
    return response;
  } catch (error) {
    console.error('Get Checklist Templates Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch checklist templates',
    };
  }
};

/**
 * Fetch all crew roles
 * @returns {Promise<Object>} { success, data: [{role_id, role_name}], error }
 */
export const getCrewRoles = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.MASTER_DATA.ROLES);
    return response;
  } catch (error) {
    console.error('Get Crew Roles Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch crew roles',
    };
  }
};

/**
 * Fetch all skills
 * @returns {Promise<Object>} { success, data: [{skill_id, skill_name}], error }
 */
export const getSkills = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.MASTER_DATA.SKILLS);
    return response;
  } catch (error) {
    console.error('Get Skills Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch skills',
    };
  }
};

/**
 * Fetch all certifications
 * @returns {Promise<Object>} { success, data: [{certification_id, certification_name}], error }
 */
export const getCertifications = async () => {
  try {
    const response = await api.get(API_ENDPOINTS.MASTER_DATA.CERTIFICATIONS);
    return response;
  } catch (error) {
    console.error('Get Certifications Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch certifications',
    };
  }
};

/**
 * Fetch all master data at once
 * @returns {Promise<Object>} { success, data: { categories, checklists, roles, skills, certifications }, error }
 */
export const getAllMasterData = async () => {
  try {
    // Fetch all master data in parallel
    const [categories, checklists, roles, skills, certifications] = await Promise.all([
      getEquipmentCategories(),
      getChecklistTemplates(),
      // getCrewRoles(),
      getSkills(),
      getCertifications(),
    ]);

    // Check if any requests failed
    const hasError = !categories.success || !checklists.success || !roles.success || !skills.success || !certifications.success;

    if (hasError) {
      return {
        success: false,
        data: null,
        error: 'Failed to fetch some master data',
      };
    }

    return {
      success: true,
      data: {
        categories: categories.data || [],
        checklists: checklists.data || [],
        roles: roles.data || [],
        skills: skills.data || [],
        certifications: certifications.data || [],
      },
      error: null,
    };
  } catch (error) {
    console.error('Get All Master Data Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch master data',
    };
  }
};


/**
 * Fetch all equipments
 * @returns {Promise<Object>} { success, data: [{role_id, role_name}], error }
 */
export const getEquipmentSuggestions = async (queryParams = {}) => {
  try {
    const response = await api.get(
      API_ENDPOINTS.MASTER_DATA.EQUIPMENT_SUGGESTIONS,
      {
        params: queryParams,
      }
    );

    return response;
  } catch (error) {
    console.error('Get Equipment Suggestions Error:', error);
    return {
      success: false,
      data: null,
      error: 'Failed to fetch equipment suggestions',
    };
  }
};
